import { useInfiniteQuery, useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { produce } from 'immer';
import * as React from 'react';
// TODO(mike): Coalesce duplicate code:
// - perUseWrappedQueryFn: used for query, infiniteQuery, and mutation
// - errorWrappedQueryFn: used for query, infiniteQuery, and mutation
const identity = (x) => x;
/** Generic */
export const getGenericQueryRouteFilter = (transport, rpcName) => [transport, rpcName];
export const getGenericQueryKey = (transport, rpcName, argOverrideFn) => (apiArg) => {
    const argKey = !!argOverrideFn ? produce(apiArg, argOverrideFn) : apiArg;
    return [transport, rpcName, argKey];
};
export const getGenericMutationKey = (transport, rpcName) => () => {
    return [transport, rpcName];
};
const useGenericQuery = (queryKey, queryFn, options) => {
    return useQuery(queryKey, queryFn, options);
};
const useGenericInfiniteQuery = (queryKey, infQueryFn, options) => {
    return useInfiniteQuery(queryKey, infQueryFn, options);
};
const useGenericMutation = (mutationKey, mutationFn, options) => {
    return useMutation(mutationKey, mutationFn, options);
};
export const createGenericQuery = ({ queryRouteFilter, getQueryKey, queryFn, optionsFn, }) => {
    const _optionsFn = optionsFn !== null && optionsFn !== void 0 ? optionsFn : identity;
    const useQuery = (arg, options, extraOptions) => {
        const queryClient = useQueryClient();
        const optionsWithDefaults = _optionsFn(options !== null && options !== void 0 ? options : {}, { queryClient, arg });
        // This allows a component using the useQuery hook to inject code within the queryFn
        // to surround the request logic (useful for performance & event metrics loggings)
        const perUseWrappedQueryFn = React.useCallback((..._outerArgs) => (...innerArgs) => {
            // Initialize request context
            const outerArgs = [..._outerArgs];
            outerArgs[1] = { ..._outerArgs[1], reqCtx: {} };
            const { reqCtx } = outerArgs[1];
            if (!(extraOptions === null || extraOptions === void 0 ? void 0 : extraOptions.perUseQueryFnWrapper)) {
                return queryFn(...outerArgs)(...innerArgs);
            }
            const execQueryFn = () => queryFn(...outerArgs)(...innerArgs);
            return extraOptions.perUseQueryFnWrapper(execQueryFn, arg, reqCtx);
        }, [extraOptions, arg]);
        // The "per-use" wrapped query function receives a queryClient parameter so that it
        // can update the client with any side-effects (for example, updating other queries)
        return useGenericQuery(getQueryKey(arg.apiArg), perUseWrappedQueryFn(arg, { queryClient, reqCtx: { uNiNiTiAlIzEd: true } }), // `reqCtx` initalized above, this is just to make typechecker happy
        optionsWithDefaults);
    };
    return {
        queryRouteFilter,
        getQueryKey,
        queryFn,
        useQuery,
        fetchQuery: (queryClient, arg, options) => {
            const optionsWithDefaults = _optionsFn(options !== null && options !== void 0 ? options : {}, { queryClient, arg });
            return queryClient.fetchQuery(getQueryKey(arg.apiArg), queryFn(arg, { queryClient, reqCtx: { uNiNiTiAlIzEd: true } }), optionsWithDefaults);
        },
        prefetchQuery: (queryClient, arg, options) => {
            const optionsWithDefaults = _optionsFn(options !== null && options !== void 0 ? options : {}, { queryClient, arg });
            return queryClient.prefetchQuery(getQueryKey(arg.apiArg), queryFn(arg, { queryClient, reqCtx: { uNiNiTiAlIzEd: true } }), optionsWithDefaults);
        },
        setQueryData: (queryClient, apiArg, data) => {
            queryClient.setQueryData(getQueryKey(apiArg), data);
        },
        getQueryData: (queryClient, apiArg) => {
            return queryClient.getQueryData(getQueryKey(apiArg));
        },
        invalidateQueries: (queryClient, apiArg) => {
            const filter = !!apiArg ? getQueryKey(apiArg) : queryRouteFilter;
            return queryClient.invalidateQueries(filter);
        },
        refetchQueries: (queryClient, apiArg) => {
            const filter = !!apiArg ? getQueryKey(apiArg) : queryRouteFilter;
            return queryClient.refetchQueries(filter);
        },
        removeQueries: (queryClient, apiArg) => {
            const filter = !!apiArg ? getQueryKey(apiArg) : queryRouteFilter;
            return queryClient.removeQueries(filter);
        },
        cancelQueries: (queryClient, apiArg) => {
            const filter = !!apiArg ? getQueryKey(apiArg) : queryRouteFilter;
            return queryClient.cancelQueries(filter);
        },
    };
};
export const createGenericInfiniteQuery = ({ queryRouteFilter, getQueryKey, queryFn, optionsFn, }) => {
    const _optionsFn = optionsFn !== null && optionsFn !== void 0 ? optionsFn : identity;
    const useInfiniteQuery = (arg, options, extraOptions) => {
        const queryClient = useQueryClient();
        const optionsWithDefaults = _optionsFn(options !== null && options !== void 0 ? options : {}, {
            queryClient,
            arg,
        });
        // This allows a component using the useInfiniteQuery hook to inject code within the queryFn
        // to surround the request logic (useful for performance & event metrics loggings)
        const perUseWrappedQueryFn = React.useCallback((..._outerArgs) => (...innerArgs) => {
            // Initialize request context
            const outerArgs = [..._outerArgs];
            outerArgs[1] = { ..._outerArgs[1], reqCtx: {} };
            const { reqCtx } = outerArgs[1];
            if (!(extraOptions === null || extraOptions === void 0 ? void 0 : extraOptions.perUseQueryFnWrapper)) {
                return queryFn(...outerArgs)(...innerArgs);
            }
            const execQueryFn = () => queryFn(...outerArgs)(...innerArgs);
            return extraOptions.perUseQueryFnWrapper(execQueryFn, arg, reqCtx);
        }, [extraOptions, arg]);
        // The "per-use" wrapped query function receives a queryClient parameter so that it
        // can update the client with any side-effects (for example, updating other queries)
        return useGenericInfiniteQuery(getQueryKey(arg.apiArg), perUseWrappedQueryFn(arg, { queryClient, reqCtx: { uNiNiTiAlIzEd: true } }), optionsWithDefaults);
    };
    return {
        queryRouteFilter,
        getQueryKey,
        queryFn,
        useInfiniteQuery,
        fetchInfiniteQuery: (queryClient, arg, options) => {
            const optionsWithDefaults = _optionsFn(options !== null && options !== void 0 ? options : {}, {
                queryClient,
                arg,
            });
            return queryClient.fetchInfiniteQuery(getQueryKey(arg.apiArg), queryFn(arg, { queryClient, reqCtx: { uNiNiTiAlIzEd: true } }), optionsWithDefaults);
        },
        prefetchInfiniteQuery: (queryClient, arg, options) => {
            const optionsWithDefaults = _optionsFn(options !== null && options !== void 0 ? options : {}, {
                queryClient,
                arg,
            });
            return queryClient.prefetchInfiniteQuery(getQueryKey(arg.apiArg), queryFn(arg, { queryClient, reqCtx: { uNiNiTiAlIzEd: true } }), optionsWithDefaults);
        },
        setQueryData: (queryClient, arg, data) => {
            queryClient.setQueryData(getQueryKey(arg), data);
        },
        invalidateQueries: (queryClient, arg) => {
            const filter = !!arg ? getQueryKey(arg) : queryRouteFilter;
            return queryClient.invalidateQueries(filter);
        },
        removeQueries: (queryClient, arg) => {
            const filter = !!arg ? getQueryKey(arg) : queryRouteFilter;
            return queryClient.removeQueries(filter);
        },
    };
};
export const createGenericMutation = ({ getMutationKey, mutationFn, optionsFn, }) => {
    const _optionsFn = optionsFn !== null && optionsFn !== void 0 ? optionsFn : identity;
    const useMutation = (options, extraOptions) => {
        const queryClient = useQueryClient();
        const optionsWithDefaults = _optionsFn(options !== null && options !== void 0 ? options : {}, { queryClient });
        // This allows a component using the useMutation hook to inject code within the mutationFn
        // to surround the request logic (useful for performance & event metrics loggings)
        const perUseWrappedMutationFn = React.useCallback((..._outerArgs) => (...innerArgs) => {
            // Initialize request context
            const outerArgs = [..._outerArgs];
            outerArgs[0] = { ..._outerArgs[0], reqCtx: {} };
            const { reqCtx } = outerArgs[0];
            if (!(extraOptions === null || extraOptions === void 0 ? void 0 : extraOptions.perUseMutationFnWrapper)) {
                return mutationFn(...outerArgs)(...innerArgs);
            }
            const execMutationFn = () => mutationFn(...outerArgs)(...innerArgs);
            return extraOptions.perUseMutationFnWrapper(execMutationFn, reqCtx);
        }, [extraOptions]);
        // The "per-use" wrapped mutation function receives a queryClient parameter so that it
        // can update the client with any side-effects (for example, updating other queries)
        return useGenericMutation(getMutationKey(), perUseWrappedMutationFn({ queryClient, reqCtx: { uNiNiTiAlIzEd: true } }), optionsWithDefaults);
    };
    return { getMutationKey, mutationFn, useMutation };
};
//# sourceMappingURL=helpers.preenvesnextjs.map
